import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Whether Shopify allows the line item to be removed.
   * @returns {boolean}
   */
  get canRemove() {
    return this.dataset.canRemove !== 'false';
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are managed client-side while preserving Shopify's restrictions.
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    minusButton.disabled =
      this.serverDisabledMinus || value < min || (value === min && !this.canRemove);
    plusButton.disabled = this.serverDisabledPlus || (effectiveMax !== null && value >= effectiveMax);
  }

  /**
   * Decrements a cart line, or removes it when it is already at its minimum quantity.
   * @param {number} stepMultiplier - Positive for increase, negative for decrease
   */
  updateQuantity(stepMultiplier) {
    const { quantityInput } = this.refs;
    const { min, value } = this.getCurrentValues();

    if (stepMultiplier < 0 && value <= min) {
      if (!this.canRemove) {
        this.updateButtonStates();
        return;
      }

      quantityInput.value = '0';
      this.onQuantityChange();
      this.updateButtonStates();
      return;
    }

    super.updateQuantity(stepMultiplier);
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
